import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Text, TextType } from 'ui-components/components/text';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Segment } from '../../services/analytics-services/segment';
import { SHOPIFY_SUPPLIES_LINK_CLICKED } from '../../constants/analytics-events';
import { SUPPLIES_DISPLAY_LOCATION, SUPPLIES_ANALYTICS_CATEGORIES } from '../home-page/version2/supplies-cards/supplies-page-constants';
import Page from '../../components/page';
import { ProductCard } from './components/product-card/product-card';
import Global from '../../store/global-selectors';
import { getPrioritizedSuppliesProducts } from '../home-page/version2/supplies-cards/supplies-prioritization.service';
import { messages } from './messages';
import style from './supplies-page.module.less';
import hardwareImage from './images/hardware.png';
import packagingImage from './images/packaging.png';
import labelsImage from './images/labels.png';

const SUPPLIES_URLS = {
  HARDWARE: 'https://supplies.shipstation.com/collections/hardware',
  LABELS: 'https://supplies.shipstation.com/collections/shipping-labels',
  PACKAGING: 'https://supplies.shipstation.com/collections/packaging',
};

export const SuppliesPage = () => {
  const products = useSelector(Global.selectSuppliesProducts);
  const isLoading = useSelector(Global.selectSuppliesIsLoading);
  const error = useSelector(Global.selectSuppliesError);

  const prioritizedProducts = useMemo(() => getPrioritizedSuppliesProducts(SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE, products), [products]);

  const handleShopHardware = useCallback(() => {
    Segment.trackEvent(SHOPIFY_SUPPLIES_LINK_CLICKED, {
      page: SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE,
      category: SUPPLIES_ANALYTICS_CATEGORIES.HARDWARE,
    });
    window.open(SUPPLIES_URLS.HARDWARE, '_blank', 'noopener,noreferrer');
  }, []);

  const handleShopLabels = useCallback(() => {
    Segment.trackEvent(SHOPIFY_SUPPLIES_LINK_CLICKED, {
      page: SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE,
      category: SUPPLIES_ANALYTICS_CATEGORIES.LABELS,
    });
    window.open(SUPPLIES_URLS.LABELS, '_blank', 'noopener,noreferrer');
  }, []);

  const handleShopPackaging = useCallback(() => {
    Segment.trackEvent(SHOPIFY_SUPPLIES_LINK_CLICKED, {
      page: SUPPLIES_DISPLAY_LOCATION.SUPPLIES_PAGE,
      category: SUPPLIES_ANALYTICS_CATEGORIES.PACKAGING,
    });
    window.open(SUPPLIES_URLS.PACKAGING, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <Page>
      <div className={style.pageContainer}>
        <div className={style.contentContainer}>
          <FlexColumn gap={Gap.Space24}>
            <Text textType={TextType.H1} value={<FormattedMessage {...messages.pageHeading} />} />
            <Text className={style.subCopy} textType={TextType.Description} value={<FormattedMessage {...messages.subCopy} />} />

            <div className={style.productGrid}>
              {isLoading && <Text textType={TextType.Description} value={<FormattedMessage {...messages.loadingProducts} />} />}
              {error && (
                <Text textType={TextType.Description} value={<FormattedMessage {...messages.errorLoadingProducts} values={{ error }} />} />
              )}
              {!isLoading &&
                !error &&
                prioritizedProducts
                  .slice(0, 8)
                  .map((product) => (
                    <ProductCard
                      key={product.id}
                      compareAtPrice={product.compareAtPrice ? parseFloat(product.compareAtPrice) : undefined}
                      imageAlt={product.secondaryImageAlt ? product.secondaryImageAlt : product.featuredImageAlt}
                      imageUrl={product.secondaryImageUrl ? product.secondaryImageUrl : product.featuredImageUrl}
                      price={parseFloat(product.price)}
                      productId={product.id}
                      productName={product.title}
                      variantId={product.variantId}
                    />
                  ))}
            </div>

            <FlexColumn gap={Gap.Space24}>
              <Text textType={TextType.H2} value={<FormattedMessage {...messages.exploreMoreHeading} />} />
              <div className={style.exploreMoreGrid}>
                <div className={style.imageContainer}>
                  <FlexRow className={style.exploreImageWrapper} style={{ backgroundImage: `url(${hardwareImage})` }}>
                    <FlexColumn gap={Gap.Space12}>
                      <Text className={style.imageLabel} textType={TextType.H3} value={<FormattedMessage {...messages.hardwareLabel} />} />
                      <Button onClick={handleShopHardware} theme={THEME_CONSTANTS.secondary}>
                        <Text textType={TextType.Caption} value={<FormattedMessage {...messages.shopHardware} />} />
                      </Button>
                    </FlexColumn>
                  </FlexRow>
                </div>
                <div className={style.imageContainer}>
                  <FlexRow className={style.exploreImageWrapper} style={{ backgroundImage: `url(${labelsImage})` }}>
                    <FlexColumn gap={Gap.Space12}>
                      <Text className={style.imageLabel} textType={TextType.H3} value={<FormattedMessage {...messages.labelsLabel} />} />
                      <Button onClick={handleShopLabels} theme={THEME_CONSTANTS.secondary}>
                        <Text textType={TextType.Caption} value={<FormattedMessage {...messages.shopLabels} />} />
                      </Button>
                    </FlexColumn>
                  </FlexRow>
                </div>
                <div className={style.imageContainer}>
                  <FlexRow className={style.exploreImageWrapper} style={{ backgroundImage: `url(${packagingImage})` }}>
                    <FlexColumn gap={Gap.Space12}>
                      <Text className={style.imageLabel} textType={TextType.H3} value={<FormattedMessage {...messages.packagingLabel} />} />
                      <Button onClick={handleShopPackaging} theme={THEME_CONSTANTS.secondary}>
                        <Text textType={TextType.Caption} value={<FormattedMessage {...messages.shopPackaging} />} />
                      </Button>
                    </FlexColumn>
                  </FlexRow>
                </div>
              </div>
            </FlexColumn>
          </FlexColumn>
        </div>
      </div>
    </Page>
  );
};
